import type { InvestmentStatus } from "@eu.mdotm.api/sphere";
import { customObjectEntriesFn, qualifier } from "$root/utils";
import {
	AutoSortTable,
	Dialog,
	DialogFooter,
	type DialogProps,
	Row,
	type TableColumn,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { AsOfButton } from "$root/pages/PortfolioDetails/as-of/AsOfButton";
import {
	type AsOfSnapshotState,
	asOfNotice,
	useAsOfSnapshot,
} from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { Map } from "immutable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";

export type InvestmentTemplateChooser = { name?: string; uuid?: string; status?: InvestmentStatus };
export type TemplateChooserUserPreferenceValue = { current: boolean; enhance: boolean };
export type TemplateChooserUserPreferenceMap = Map<string, TemplateChooserUserPreferenceValue>;
export type TemplateChooserSubmitParams = { name?: string; uuid: string; choice: "current" | "enhance" };
export type PortfolioTemplateChooserDialogProps = {
	investments: Array<InvestmentTemplateChooser>;
	onSubmitAsync(investments: Array<TemplateChooserSubmitParams>, opts: { asOf: string | undefined }): Promise<void>;
	/**
	 * As-of date (`YYYY-MM-DD`) the Current report is taken at; unset = today. The proposal is always
	 * today, so the date reaches only the `current` pages — `generateReportBuilderTemplateUrl` is where
	 * that split is made.
	 */
	asOf?: string;
	/** Absent where the surface offers no date; the caller holds the snapshot it commissions. */
	setAsOf?(asOf: string | null): void;
	/** Whether `asOf`'s snapshot can be read yet; absent in a dialog that offers no date. */
	snapshot?: AsOfSnapshotState;
	/** Portfolio inception date — the day after it is the earliest date the picker offers. */
	inceptionDate?: string;
	/** The one report each proposal-ready row opens checked; unset = Current. */
	initialChoice?: TemplateChooserSubmitParams["choice"];
} & Pick<DialogProps, "onAnimationStateChange" | "onClose" | "show">;

const PortfolioTemplateChooserDialog = (props: PortfolioTemplateChooserDialogProps): React.JSX.Element => {
	const { t } = useTranslation();

	const proposalReadyInvestments = useMemo(() => {
		return props.investments.filter((investment) => investment.status === "PROPOSAL_READY");
	}, [props.investments]);

	const [userPreference, setUserPreference] = useState<TemplateChooserUserPreferenceMap>(() => {
		const choice = props.initialChoice ?? "current";
		return Map(
			proposalReadyInvestments.map(({ uuid }) => [
				uuid!,
				{ current: choice === "current", enhance: choice === "enhance" },
			]),
		);
	});

	const { asOf, setAsOf, snapshot } = props;
	// The date is only ever asked of the Current report, so a dialog submitting the proposal alone has
	// nothing to wait for even while a date sits in the picker.
	const currentSelected = userPreference.some((x) => x.current);
	// The PDF pipeline waits on a snapshot that is still generating, so only a failed one has nothing
	// left to wait for. Same rule the history header's report action applies; a snapshot that has to be
	// readable before the click is the composition xls, not this.
	const snapshotFailed = currentSelected && asOf != null && snapshot?.kind === "error";
	// A row that is not proposal-ready always submits Current, so only an all-proposal-ready dialog can submit nothing.
	const nothingChosen =
		proposalReadyInvestments.length === props.investments.length &&
		userPreference.every((x) => !x.current && !x.enhance);
	const notice = asOfNotice(asOf, snapshot);
	// The whole sentence, not the bar's short form: there is room for it under the table.
	const snapshotNotice =
		notice == null
			? null
			: notice.qualifier === "Generating"
				? { text: t("PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATING"), qualifier: notice.qualifier }
				: {
						text: t(
							notice.reason === "timeout"
								? "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_TIMEOUT"
								: "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_FAILED",
						),
						qualifier: notice.qualifier,
					};

	// One date for the dialog, rendered in each row's Current cell.
	const { inceptionDate } = props;
	const renderAsOf = useMemo(
		() =>
			setAsOf
				? () => (
						<AsOfButton
							asOf={asOf}
							setAsOf={setAsOf}
							snapshot={snapshot}
							inceptionDate={inceptionDate}
							qualifier={qualifier.portfolioDetails.templateChooser.asOfDate}
							label={{ type: "Body/S/BOLD-UPPERCASE", color: themeCSSVars.palette_N500 }}
						/>
					)
				: undefined,
		[asOf, setAsOf, snapshot, inceptionDate],
	);

	return (
		<Dialog
			show={props.show}
			onClose={props.onClose}
			onAnimationStateChange={props.onAnimationStateChange}
			header="Choose which type of report you want generate"
			onSubmitAsync={async () => {
				const onSubmitAsyncParams = props.investments.flatMap((x): TemplateChooserSubmitParams[] => {
					const preference = userPreference.get(x.uuid!);
					if (!preference) {
						return [{ name: x.name, uuid: x.uuid!, choice: "current" }];
					}

					return customObjectEntriesFn(preference).flatMap(([key, choice]) =>
						choice ? [{ name: x.name, uuid: x.uuid!, choice: key }] : [],
					);
				});

				await props.onSubmitAsync(onSubmitAsyncParams, { asOf });
			}}
			footer={({ state }) => (
				<DialogFooter
					primaryAction={
						<SubmitButton palette="primary" disabled={snapshotFailed || nothingChosen}>
							{t("BUTTON.SAVE_PROCEED")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" disabled={state === "loading"} onClick={props.onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			)}
			size="large"
		>
			<p className="mb-4">
				These portfolio are on <strong>Proposal Ready</strong> status choose the report template you want to generate
			</p>
			<TemplateChooserTable
				proposalReadyInvestments={proposalReadyInvestments}
				userPreference={userPreference}
				onChangePreference={setUserPreference}
				renderAsOf={renderAsOf}
			/>
			{setAsOf && (
				// One line, present from the moment a date can be picked: the notice arrives minutes after the
				// pick, and a line that appears then would resize the dialog under the cursor. All three
				// messages are one line at this width, so the reserved height is the height they need.
				<p
					className="mt-3"
					style={{
						color: snapshotNotice?.qualifier === "Failed" ? themeCSSVars.palette_D500 : themeCSSVars.palette_N500,
					}}
					data-qualifier={qualifier.portfolioDetails.templateChooser.asOfNotice(snapshotNotice?.qualifier ?? "Idle")}
				>
					{snapshotNotice?.text ?? "\u00a0"}
				</p>
			)}
		</Dialog>
	);
};

export function TemplateChooserTable<T extends InvestmentTemplateChooser>({
	proposalReadyInvestments,
	userPreference,
	onChangePreference,
	renderAsOf,
}: {
	proposalReadyInvestments: T[];
	userPreference: TemplateChooserUserPreferenceMap;
	onChangePreference(userPreference: TemplateChooserUserPreferenceMap): void;
	/** Renders a date control beside the Current checkbox, in its cell. */
	renderAsOf?(): React.ReactNode;
}): React.JSX.Element {
	const { t } = useTranslation();
	const onChangePreferenceRef = useUnsafeUpdatedRef(onChangePreference);

	const columns = useMemo<Array<TableColumn<InvestmentTemplateChooser>>>(
		() => [
			{
				header: t("TABLE.HEADERS.NAME"),
				content: ({ name }) => name ?? "",
				sortFn: builtInSortFnFor("name"),
				name: "portfolio name",
				minWidth: 180,
			},
			{
				header: (headerProps) => (
					<Row {...headerProps} alignItems="center">
						<Checkbox
							checked={
								userPreference.filter((x) => x.current).size === proposalReadyInvestments.length
									? true
									: userPreference.filter((x) => x.current).size === 0
										? false
										: "indeterminate"
							}
							onChange={(selection) =>
								onChangePreferenceRef.current(
									Map(
										proposalReadyInvestments.map((investment) => {
											const preference = userPreference.get(investment.uuid!);
											if (!preference) {
												return [investment.uuid!, { current: selection, enhance: false }];
											}
											return [investment.uuid!, { current: selection, enhance: preference.enhance }];
										}),
									),
								)
							}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
								Current
							</Text>
						</Checkbox>
					</Row>
				),
				content: (row, cellProps) => {
					const preference = userPreference.get(row.uuid!);
					const toggleCurrent = () =>
						onChangePreferenceRef.current(
							userPreference.update(row.uuid!, (x) => ({
								current: !x?.current,
								enhance: x?.enhance ?? false,
							})),
						);

					return (
						<Row {...cellProps} alignItems="center" gap={8}>
							<Checkbox checked={preference?.current ?? false} onChange={toggleCurrent} />
							{renderAsOf?.()}
						</Row>
					);
				},
				name: "current choice",
				minWidth: renderAsOf ? 220 : 100,
			},
			{
				header: (headerProps) => (
					<Row {...headerProps} alignItems="center">
						<Checkbox
							checked={
								userPreference.filter((x) => x.enhance).size === proposalReadyInvestments.length
									? true
									: userPreference.filter((x) => x.enhance).size === 0
										? false
										: "indeterminate"
							}
							onChange={(selection) =>
								onChangePreferenceRef.current(
									Map(
										proposalReadyInvestments.map((investment) => {
											const preference = userPreference.get(investment.uuid!);
											if (!preference) {
												return [investment.uuid!, { current: false, enhance: selection }];
											}
											return [investment.uuid!, { current: preference.current, enhance: selection }];
										}),
									),
								)
							}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500}>
								Proposal
							</Text>
						</Checkbox>
					</Row>
				),
				content: (row, cellProps) => {
					const preference = userPreference.get(row.uuid!);
					return (
						<Row {...cellProps} alignItems="center">
							<Checkbox
								checked={preference?.enhance ?? false}
								onChange={() =>
									onChangePreferenceRef.current(
										userPreference.update(row.uuid!, (x) => ({
											current: x?.current ?? false,
											enhance: !x?.enhance,
										})),
									)
								}
							/>
						</Row>
					);
				},
				name: "enhance choice",
				minWidth: 100,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[proposalReadyInvestments, t, userPreference, onChangePreferenceRef, renderAsOf],
	);

	return <AutoSortTable columns={columns} rows={proposalReadyInvestments} style={{ maxHeight: 410 }} />;
}

/** Holds the dialog's as-of date and the snapshot picking one commissions. */
function PortfolioTemplateChooserDialogWithAsOf({
	offerAsOf,
	...props
}: Omit<PortfolioTemplateChooserDialogProps, "asOf" | "setAsOf" | "snapshot"> & {
	/** False where the report takes no date; unset offers one to a single portfolio. */
	offerAsOf?: boolean;
}): React.JSX.Element {
	const [asOf, setAsOf] = useState<string | null>(null);
	// One date per dialog, so one portfolio's snapshot. A multi-portfolio chooser therefore offers no
	// date: N dates would commission N multi-minute generations and hold the dialog on the slowest.
	const uuid = props.investments.length === 1 ? props.investments[0].uuid : undefined;
	const snapshot = useAsOfSnapshot(uuid, asOf ?? undefined);

	return (
		<PortfolioTemplateChooserDialog
			{...props}
			asOf={asOf ?? undefined}
			setAsOf={uuid == null || offerAsOf === false ? undefined : setAsOf}
			snapshot={snapshot}
		/>
	);
}

type spawnPortfolioTemplateChooserProps = Omit<
	React.ComponentProps<typeof PortfolioTemplateChooserDialogWithAsOf>,
	"onClose" | "show"
>;
export function spawnPortfolioTemplateChooser(params: spawnPortfolioTemplateChooserProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<PortfolioTemplateChooserDialogWithAsOf
				{...params}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (portfolio, opts) => {
					await params.onSubmitAsync(portfolio, opts);
					resolve();
				}}
			/>
		)),
	);
}

export default PortfolioTemplateChooserDialog;
